import { ResourceLanguage } from 'i18next';

const translations: ResourceLanguage = {
  app: {
    titleBLS: 'Registration - Bikeleasing',
    titleProbonio: 'Registration - Probonio',
  },
  common: {
    or: 'or',
    actions: {
      back: 'Back',
      next: 'Next',
      backToHomepage: 'Back to Homepage',
      close: 'Close',
    },
    errors: {
      networkError: 'A network error occurred',
      rateLimitExceeded: 'Too many requests. Please try again later.',
      unknownError: 'An unknown error has occurred',
    },
    lang: {
      en: 'English',
      de: 'German',
    },
  },
  accessibility: {
    login: 'Login',
    signInGoogle: 'Sign in with Google',
    signInMicrosoft: 'Sign in with Microsoft',
    signInApple: 'Sign in with Apple',
    closeDialog: 'Close dialog',
    copyEmail: 'Copy support email',
  },
  auth: {
    title: 'Authentication in progress',
    message: 'We are currently logging you in and preparing everything. This may take a moment. Please do not reload the page.',
  },
  footer: {
    privacy: 'Privacy Policy',
    support: 'Support',
    imprint: {
      title: 'Imprint',
      companyName: {
        title: 'Company Name:',
        content: 'BLS Benefit Leasing Service Plattform GmbH',
      },
      representedBy: {
        title: 'Represented by:',
        content: 'COO Othmane Khelil',
      },
      address: {
        title: 'Address:',
        content: 'Business Address: Bewdley-Platz 18<br/>34246 Vellmar',
      },
      contact: {
        title: 'Contact:',
        content: 'Phone: <0>{{phone}}</0><br/>E-Mail: <1>{{email}}</1>',
      },
      registerEntry: {
        title: 'Register Entry:',
        content: 'Registered in the commercial register.<br/>Register court: Amtsgericht Kassel<br/>Commercial register number: HRB 20519',
      },
      vatId: 'VAT ID according to § 27a UStG:',
      editorial: {
        title: 'Editorially responsible according to § 18 Abs. 2 MStV:',
        content: 'Othmane Khelil, Bewdley-Platz 18, 34246 Vellmar',
      },
    },
  },
  notFound: {
    title: 'Page not found',
    message: 'The requested page does not exist or has been moved. Please check the URL or return to the homepage.',
  },
  registration: {
    title: 'Registration',
    message:
      'You can obtain the company code from your employer. If this is not the case, please check your emails or contact your employer directly.',
    employerCodeLabel: 'Company code',
    employerCodeHelpProbonio: 'Code with 4, 8, or 12 characters',
    employerCodeHelpBLS: 'Code with 4 characters',
    employer: 'Employer: {{companyName}}',
    choosePreferredMethod: 'Please decide on your preferred registration method, which you will also use for your login in the future.',
    consent: {
      privacyPolicy: 'Please refer to our <0>privacy policy</0> to get information on how we process your data.',
      marketingConsent:
        'I agree that the companies of BLS Beteiligungs GmbH may personally inform and advise me about interesting offers and news in the areas of company bike leasing, insurance, employee benefits, and used bike sales. My consent is voluntary and can be revoked at any time. <0>More details</0>',
    },
    alreadyAnAccount: 'Already have an account?',
    alreadySignedIn: 'You are already logged in:',
    dividerLabel: 'or continue with',
    completeData: 'Complete data',
    firstName: 'First name',
    lastName: 'Last name',
    phoneNumber: 'Phone number',
    phoneNumberInvalid: 'Invalid phone number. Example of a valid phone number: {{EXAMPLE_VALID_PHONE_NUMBER}}',
    address: {
      street: 'Street and house number',
      postalCode: 'Postal code',
      city: 'City',
      country: 'Country',
    },
    employeeRecord: {
      employeeNumber: 'Employee number',
      costCenter: 'Cost center',
      bikeleasing: {
        employeeNumber: 'Personnel Number / ValueNet-ID',
        employeeNumberHint:
          'If the company is associated with the Bikeleasing service via ValueNet, the ValueNet ID instead of the personnel number is mandatory.',
        costCenterPlaceholder: '0 if not known',
      },
    },
    actions: {
      register: 'Register with email',
      login: 'Click here!',
      finish: 'Finish registration',
      switchAccount: 'Sign in with a different account?',
    },
    errors: {
      invalidCode: 'The entered code is invalid or has already been used',
      expiredCode: 'The entered code has expired',
    },
  },
  errorDialog: {
    title: 'Oops! Something went wrong',
    alertMessage:
      "If the problem persists, you may have reached the system's rate limit. Try again in a few minutes, or contact support for assistance.",
    actions: {
      contactSupport: 'Contact Support',
      restartRegistration: 'Restart Registration',
      loginAgain: 'Log in again',
    },
    messages: {
      redeemError:
        "We couldn't link your registration code to your account. This may happen if the code has already been used, or due to a temporary system issue. Please try restarting the registration process. Make sure your registration code is correct and that your internet connection is stable.",
      employeeEmailAlreadyExists:
        'The email address associated with this registration code already exists in our system. This can happen if you have already registered or if there is an issue with the registration code. Please try logging in with your email address or contact support for assistance.',
      registrationCodeEmployeeMismatch:
        "You're already registered as a different employee at this company with this account. This registration code belongs to a different employee profile. Please log in with the correct account, or contact your employer if you need help.",
      employeeError:
        "We couldn't load your account details. This usually happens if your session has expired or due to a temporary system issue. Please try logging in again. Make sure your internet connection is stable.",
    },
  },
  mailNotVerified: {
    alert: 'Your email address has not been verified yet',
    message:
      "To continue, please verify your email address <strong>{{email}}</strong>. Once you've completed the verification, use the button below to continue.",
    checkAgain: 'Check again',
  },
  redeemCode: {
    title: 'Redeeming your code',
    message: 'We are processing your registration code and preparing your account. This may take a moment. Please do not reload the page.',
  },
  registrationComplete: {
    title: 'Thanks for your registration',
    login: 'Login',
    autoApproveMessage:
      'Your account has been successfully created and is now active. You can now log in to {{app}} using your chosen method.',
    manualApproveMessage:
      'Once your employer has approved your account, you will receive an email and will be able to log in to {{app}} using your chosen method.',
  },
  checkRegistrationLink: {
    title: 'Registration',
    message: 'We are checking your registration link. This may take a moment. Please do not reload the page.',
    actions: {
      login: 'Log in to {{app}}',
      useCodeInstead: 'Use code to register instead',
    },
    errors: {
      title: 'Invalid registration link',
      invalidLink:
        'The registration link is invalid or has already been used. This can happen if you have accessed the link multiple times or if the link was transmitted incorrectly.',
      expiredLink:
        'The registration link has expired. Registration links are only valid for a limited time to ensure the security of your account.',
      alertText: 'Please contact your employer to obtain a new link. If you already have an account, please log in here.',
    },
  },
  completeProfileAfterRegistration: {
    title: 'Searching for incomplete profile',
    message:
      'We are checking if there is an incomplete profile associated with your account. This may take a moment. Please do not reload the page.',
    noEmployeeFound: {
      title: 'No incomplete profile found',
      message:
        "Good news! We couldn't find any incomplete profiles associated with your account. This means that your registration is complete, and you can log in and start using the app.",
    },
  },
  completeData: {
    errors: {
      conflict: 'The specified personnel number already exists',
      missingRequiredField: 'A required field was not filled out',
      notEligible: {
        DuplicateEmployeeNumber: 'The specified personnel number already exists',
        RegisterValidationFailed: 'Invalid input, please check your data',
        GoogleValidationFailed: 'Your data could not be found, please check your input',
        EligibilityCheckFailed: 'Your data could not be verified, please check your input or contact your employer.',
      },
    },
  },
  employerSpecialConfig: {
    app: {
      registration: {
        cost_center: {
          brk: 'Employer Number',
          location_store: 'Store Location',
        },
      },
      registration_cost_center_error: {
        porr_8_signs: 'The cost center has to be 8 numbers long.',
      },
      registration_personal_number_error: {
        enbw_len: 'Please enter your 7-digit personnel number.',
        enbw_more_than_4:
          'Your entry does not meet the formal criteria. In order to be able to ensure immediate and correct processing of your application, your correct personnel number is mandatory (see remuneration statement).',
        lufthansa_not_allowed: 'This number does not meet the formal requirements. Please check.',
        lufthansa_u123456: 'The personnel number should correspond to the pattern U012345',
        porr_between_6_and_9: 'Please be aware that your personnel number has to be between 6 and 9 numbers long.',
        title: 'Please fill in this field. If you do not know this value, simply enter 0.',
      },
      registration_personal_number_label: {
        lufthansa: 'Please note that your personnel number must be 7 characters long (U012345).',
      },
    },
    // These translations are used in the employer-specific configuration of the registration form. The keys correspond to the possible values of `registerValidation.fieldLabel` from the API response.
    Kostenstelle: 'Cost center',
    Personalnummer: 'Personnel number',
  },
};

export default translations;
